import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';

/**
 * Reusable OCR Processing Hook
 * @param {Object} options - Configuration options
 * @param {Function} options.ocrService - OCR service function to call
 * @param {string} options.consent - Consent value (default: 'Y')
 * @param {Array} options.allowedTypes - Allowed file types
 * @param {number} options.maxSize - Maximum file size in bytes
 * @param {Function} options.onSuccess - Success callback
 * @param {Function} options.onError - Error callback
 */
const useReusableOCR = (options = {}) => {
    const {
        ocrService,
        consent = 'Y',
        allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'],
        maxSize = 5 * 1024 * 1024, // 5MB default
        onSuccess,
        onError,
        extractorFunction, // Optional custom data extractor
        enableToast = true
    } = options;

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [extractedData, setExtractedData] = useState({});
    const [uploadedFile, setUploadedFile] = useState(null);
    const [rawResponse, setRawResponse] = useState(null);

    // Default data extractor
    const defaultExtractor = (data) => ({
        full_name: data.name || data.full_name || '',
        dob: data.date_of_birth || data.dob || '',
        gender: data.gender || '',
        guardian_name: data.guardian_name || '',
        address: data.address || '',
        document_id: data.document_id || data.id_number || '',
        document_type: data.document_type || 'AADHAAR'
    });

    const validateFile = useCallback((file) => {
        const errors = [];

        if (!file) {
            errors.push('No file selected');
            return { isValid: false, errors };
        }

        if (!allowedTypes.includes(file.type)) {
            errors.push(`Invalid file type. Allowed types: ${allowedTypes.map(t => t.split('/')[1]).join(', ')}`);
        }

        if (maxSize && file.size > maxSize) {
            const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(1);
            errors.push(`File size exceeds ${maxSizeMB}MB limit`);
        }

        return {
            isValid: errors.length === 0,
            errors
        };
    }, [allowedTypes, maxSize]);

    const processOCRDocument = useCallback(async (file) => {
        if (!ocrService) {
            const errorMsg = 'OCR service not configured';
            setError(errorMsg);
            if (enableToast) toast.error(errorMsg);
            return false;
        }

        setIsLoading(true);
        setError('');

        // Validate file
        const validation = validateFile(file);
        if (!validation.isValid) {
            const errorMsg = validation.errors.join(', ');
            setError(errorMsg);
            setIsLoading(false);
            if (enableToast) toast.error(errorMsg);
            return false;
        }

        try {
            const formData = new FormData();
            formData.append('file_front', file, file.name);
            formData.append('consent', consent);

            const response = await ocrService(formData);
            setRawResponse(response);

            if (response && !response.error && response.data) {
                const extractor = extractorFunction || defaultExtractor;
                const extractedInfo = extractor(response.data);
                
                setExtractedData(extractedInfo);
                setIsLoading(false);
                
                if (enableToast && response.message) {
                    toast.success(response.message);
                }

                if (onSuccess) {
                    onSuccess(extractedInfo, response);
                }

                return true;
            } else {
                const errorMessage = response?.message || 'Failed to extract document details';
                setError(errorMessage);
                setIsLoading(false);
                
                if (enableToast) {
                    toast.error(errorMessage);
                }

                if (onError) {
                    onError(errorMessage, response);
                }

                return false;
            }
        } catch (err) {
            const errorMessage = err.message || 'An unexpected error occurred';
            setError(errorMessage);
            setIsLoading(false);
            
            if (enableToast) {
                toast.error(errorMessage);
            }

            if (onError) {
                onError(errorMessage, err);
            }

            return false;
        }
    }, [ocrService, consent, validateFile, extractorFunction, enableToast, onSuccess, onError]);

    const handleFileUpload = async (file) => {
        if (!file) return false;

        setUploadedFile(file);
        setError('');
        return await processOCRDocument(file);
    };

    const resetOCR = useCallback(() => {
        setIsLoading(false);
        setError('');
        setExtractedData({});
        setUploadedFile(null);
        setRawResponse(null);
    }, []);

    const updateExtractedData = useCallback((newData) => {
        setExtractedData(prev => ({ ...prev, ...newData }));
    }, []);

    return {
        // State
        isLoading,
        error,
        extractedData,
        uploadedFile,
        rawResponse,
        
        // Actions
        handleFileUpload,
        resetOCR,
        updateExtractedData,
        validateFile,
        
        // Utils
        setError,
        setExtractedData
    };
};

export default useReusableOCR;